import { useState, useMemo } from 'react';
import { Button, TextField, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

const RolesManager = ({ roles, onSave, onCancel, isSaving = false }) => {
    const [searchTerm, setSearchTerm]   = useState('');
    const [localRoles, setLocalRoles]   = useState(roles);

    const stats = useMemo(() => {
        const selected = localRoles.filter(r => r.IsSelected).length;
        return { total: localRoles.length, selected };
    }, [localRoles]);

    const filteredRoles = useMemo(() =>
        localRoles
            .map((r, idx) => ({ ...r, originalIndex: idx }))
            .filter(r => !searchTerm || r.RoleName?.toLowerCase().includes(searchTerm.toLowerCase()))
    , [localRoles, searchTerm]);

    const handleToggle = (originalIndex) => {
        // single-select: only one role at a time
        setLocalRoles(prev => prev.map((r, idx) => ({
            ...r, IsSelected: idx === originalIndex ? !r.IsSelected : false
        })));
    };

    const handleClearAll = () => {
        setLocalRoles(prev => prev.map(r => ({ ...r, IsSelected: false })));
    };

    const handleSave = () => { onSave(localRoles); };

    return (
        <div style={{ background: 'white', display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'Times New Roman' }}>

            {/* ── Gradient Header ── */}
            <div style={{
                background: 'linear-gradient(135deg, #2C3E6E 0%, #3d5491 100%)',
                padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px',
            }}>
                <div style={{ background: 'rgba(255,255,255,0.18)', borderRadius: '7px', padding: '6px', display: 'flex' }}>
                    <AdminPanelSettingsIcon style={{ fontSize: '18px', color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'white' }}>Manage User Role</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.75)', marginTop: '1px' }}>
                        Only one role can be assigned · {stats.selected}/{stats.total} selected
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                    {[
                        { label: `${stats.total} Total`,    bg: 'rgba(255,255,255,0.18)' },
                        { label: `${stats.selected} Active`, bg: 'rgba(103,126,234,0.6)'  },
                    ].map(s => (
                        <span key={s.label} style={{ background: s.bg, color: 'white', padding: '3px 8px', borderRadius: '5px', fontSize: '10px', fontWeight: '700' }}>
                            {s.label}
                        </span>
                    ))}
                </div>
                <IconButton onClick={onCancel} size="small" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    <CloseIcon style={{ fontSize: '18px' }} />
                </IconButton>
            </div>

            {/* ── Search + Clear ── */}
            <div style={{ padding: '12px 16px 8px 16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <TextField
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search roles..."
                    size="small"
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: '16px', color: '#2C3E6E' }} /></InputAdornment>,
                        style: { fontSize: '12px', height: '32px' }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '7px', backgroundColor: '#f8f9fa', '&:hover fieldset': { borderColor: '#2C3E6E' }, '&.Mui-focused fieldset': { borderColor: '#2C3E6E' } } }}
                />
                <button
                    onClick={handleClearAll}
                    style={{
                        background: '#ffebee', color: '#c62828', border: 'none',
                        borderRadius: '6px', padding: '5px 12px', fontSize: '11px',
                        fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
                        fontFamily: 'Times New Roman',
                    }}
                >
                    ✕ Clear
                </button>
            </div>

            {/* ── Info banner ── */}
            <div style={{ margin: '0 16px 10px 16px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '7px', padding: '6px 12px', fontSize: '11px', color: '#1565c0' }}>
                ℹ️ Click a role to select it. The selected role defines what this user can access via role-level permissions.
            </div>

            {/* ── Roles list — matrix style ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px', minHeight: 0 }}>

                {/* Column header */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 60px', gap: '6px', padding: '0 8px 6px 8px', alignItems: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Role Name</div>
                    <div style={{ textAlign: 'center' }}>
                        <span style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', color: 'white', padding: '2px 10px', borderRadius: '5px', fontSize: '10px', fontWeight: '700' }}>
                            Assign
                        </span>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Status</div>
                </div>

                {filteredRoles.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#a0aec0', fontSize: '12px' }}>
                        No roles found.
                    </div>
                ) : filteredRoles.map((role, i) => (
                    <div
                        key={role.originalIndex}
                        onClick={() => handleToggle(role.originalIndex)}
                        style={{
                            display: 'grid', gridTemplateColumns: '1fr 80px 60px',
                            gap: '6px', alignItems: 'center',
                            padding: '10px 8px', marginBottom: '5px', borderRadius: '8px',
                            background: role.IsSelected ? 'rgba(103,126,234,0.07)' : i % 2 === 0 ? '#fafafa' : 'white',
                            border: `1px solid ${role.IsSelected ? '#667eea40' : '#f0f0f0'}`,
                            borderLeft: `3px solid ${role.IsSelected ? '#667eea' : '#e2e8f0'}`,
                            cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
                        }}
                    >
                        {/* Role name */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '28px', height: '28px', borderRadius: '7px', flexShrink: 0,
                                background: role.IsSelected ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#e2e8f0',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background 0.15s',
                            }}>
                                <AdminPanelSettingsIcon style={{ fontSize: '14px', color: role.IsSelected ? 'white' : '#718096' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: role.IsSelected ? '700' : '500', color: role.IsSelected ? '#667eea' : '#2d3748' }}>
                                    {role.RoleName}
                                </div>
                                <div style={{ fontSize: '10px', color: '#a0aec0' }}>Role · {role.IsSelected ? 'Assigned' : 'Not assigned'}</div>
                            </div>
                        </div>

                        {/* Radio toggle */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={{
                                width: '22px', height: '22px', borderRadius: '50%',
                                border: `2px solid ${role.IsSelected ? '#667eea' : '#e2e8f0'}`,
                                background: role.IsSelected ? 'linear-gradient(135deg,#667eea,#764ba2)' : 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.15s',
                                boxShadow: role.IsSelected ? '0 2px 6px rgba(103,126,234,0.4)' : 'none',
                            }}>
                                {role.IsSelected && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white' }} />}
                            </div>
                        </div>

                        {/* Status */}
                        <div style={{ textAlign: 'right' }}>
                            <span style={{
                                fontWeight: '700', fontSize: '11px',
                                color: role.IsSelected ? '#667eea' : '#a0aec0',
                            }}>
                                {role.IsSelected ? '✓ On' : '— Off'}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Footer ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid #f0f0f0', gap: '8px' }}>
                <div style={{ fontSize: '11px', color: '#718096' }}>
                    <span style={{ fontWeight: '700', color: stats.selected > 0 ? '#667eea' : '#a0aec0' }}>{stats.selected}</span> of{' '}
                    <span style={{ fontWeight: '700' }}>{stats.total}</span> roles selected
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button onClick={onCancel} disabled={isSaving} variant="outlined" size="small"
                        sx={{ borderColor: '#d0d7e3', color: '#4a5568', fontSize: '11px', fontWeight: '700', padding: '4px 14px', borderRadius: '7px', minHeight: '30px', textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving} variant="contained" size="small"
                        startIcon={isSaving ? <CircularProgress size={11} style={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: '13px' }} />}
                        sx={{
                            background: 'linear-gradient(135deg,#2C3E6E,#3d5491)', color: 'white',
                            fontSize: '11px', fontWeight: '700', padding: '4px 14px', borderRadius: '7px',
                            minHeight: '30px', textTransform: 'none', boxShadow: '0 2px 6px rgba(44,62,110,0.3)',
                            '&:hover': { background: 'linear-gradient(135deg,#1a2a4a,#2C3E6E)' },
                        }}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default RolesManager;
